import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Layout from '../Layout';
import auditAPI from '../services/auditAPI';
import {AlertTriangle, Calendar, Download, FileText, Loader, RefreshCw, Search, Shield, XCircle} from 'lucide-react';
import LogsTable from '../Components/LogsTable';
import SecurityEventsTable from '../Components/SecurityEventsTable';
import StatisticsPanel from '../Components/StatisticsPanel';
import ExportModal from '../Components/ExportModal';
import {Toast} from '../Components/Toast';
import DateRangePicker from '../Components/DateRangePicker';

const AuditLogsPage = () => {
    const navigate = useNavigate();

    // State for tabs
    const [activeTab, setActiveTab] = useState('logs');

    // State for data
    const [auditLogs, setAuditLogs] = useState([]);
    const [securityEvents, setSecurityEvents] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for filters
    const [filters, setFilters] = useState({
        school_id: '',
        user_email: '',
        event_type: '',
        start_date: null,
        end_date: null,
        limit: 100,
        skip: 0,
        sort_by: 'timestamp',
        sort_order: -1
    });

    // State for security filters
    const [securityFilters, setSecurityFilters] = useState({
        alert_type: '',
        severity: '',
        school_id: '',
        start_date: null,
        end_date: null,
        limit: 100,
        skip: 0
    });

    // State for pagination
    const [totalLogs, setTotalLogs] = useState(0);
    const [totalSecurityEvents, setTotalSecurityEvents] = useState(0);

    // State for modals
    const [showExportModal, setShowExportModal] = useState(false);

    // State for toast notifications
    const [toast, setToast] = useState({show: false, message: '', type: 'success'});

    // Check authentication on component mount
    useEffect(() => {
        if (!localStorage.getItem('accessToken')) {
            navigate('/?expired=true');
            return;
        }

        // If authenticated, fetch data based on active tab
        if (activeTab === 'logs') {
            fetchAuditLogs();
        } else if (activeTab === 'security') {
            fetchSecurityEvents();
        } else if (activeTab === 'statistics') {
            fetchStatistics();
        }
    }, [activeTab, navigate]);

    // Fetch audit logs
    const fetchAuditLogs = async () => {
        setLoading(true);
        setError(null);

        try {
            // Ensure we have a token before making the request
            if (!localStorage.getItem('accessToken')) {
                navigate('/?expired=true');
                return;
            }

            // Prepare the parameters using the filters state
            const params = {...filters};

            const data = await auditAPI.getAuditLogs(params);

            // The backend returns data in a specific structure
            // Map the response to match the expected format in the component
            setAuditLogs(data.items || []);
            setTotalLogs(data.count || 0);
        } catch (error) {
            console.error("Error fetching audit logs:", error);
            const errorMessage = auditAPI.handleError(error);
            setError(errorMessage);
            showToast(`Failed to fetch audit logs: ${errorMessage}`, 'error');

            // Check if it's an authentication error
            if (error.status === 401) {
                navigate('/?expired=true');
            }
        } finally {
            setLoading(false);
        }
    };

    // Fetch security events
    const fetchSecurityEvents = async () => {
        setLoading(true);
        setError(null);

        try {
            // Ensure we have a token before making the request
            if (!localStorage.getItem('accessToken')) {
                navigate('/?expired=true');
                return;
            }

            // Prepare the parameters using the securityFilters state
            const params = {...securityFilters};

            console.log('Fetching security events with params:', params);
            const data = await auditAPI.getSecurityEvents(params);
            console.log('Received security events response:', data);

            // Handle the API response structure
            if (data && Array.isArray(data.items)) {
                setSecurityEvents(data.items);
                setTotalSecurityEvents(data.count || data.items.length || 0);
            } else if (data && typeof data === 'object') {
                // If we got an object but not the expected structure
                console.warn('Unexpected API response structure:', data);

                // Try to find any array in the response
                let foundEvents = null;
                Object.keys(data).forEach(key => {
                    if (Array.isArray(data[key])) {
                        foundEvents = data[key];
                    }
                });

                if (foundEvents) {
                    setSecurityEvents(foundEvents);
                    setTotalSecurityEvents(foundEvents.length);
                } else {
                    // If no arrays found, set empty array
                    setSecurityEvents([]);
                    setTotalSecurityEvents(0);
                }
            } else {
                // If response is completely unexpected
                console.error('Invalid API response:', data);
                setSecurityEvents([]);
                setTotalSecurityEvents(0);
            }
        } catch (error) {
            console.error("Error fetching security events:", error);
            const errorMessage = auditAPI.handleError(error);
            setError(errorMessage);
            showToast(`Failed to fetch security events: ${errorMessage}`, 'error');

            // Reset events to empty array on error
            setSecurityEvents([]);
            setTotalSecurityEvents(0);

            // Check if it's an authentication error
            if (error.status === 401) {
                navigate('/?expired=true');
            }
        } finally {
            setLoading(false);
        }
    };

    // Fetch statistics
    const fetchStatistics = async () => {
        setLoading(true);
        setError(null);

        try {
            // Ensure we have a token before making the request
            if (!localStorage.getItem('accessToken')) {
                navigate('/?expired=true');
                return;
            }

            const params = {
                school_id: filters.school_id || undefined,
                days: 30 // Default to 30 days
            };

            const data = await auditAPI.getAuditStatistics(params);

            // Set the statistics data
            setStatistics(data.statistics || {});
        } catch (error) {
            console.error("Error fetching audit statistics:", error);
            const errorMessage = auditAPI.handleError(error);
            setError(errorMessage);
            showToast(`Failed to fetch audit statistics: ${errorMessage}`, 'error');

            // Check if it's an authentication error
            if (error.status === 401) {
                navigate('/?expired=true');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle filter changes
    const handleFilterChange = (newFilters) => {
        setFilters({...filters, ...newFilters, skip: 0}); // Reset pagination when filters change
    };

    // Handle security filter changes
    const handleSecurityFilterChange = (newFilters) => {
        setSecurityFilters({...securityFilters, ...newFilters, skip: 0}); // Reset pagination when filters change
    };

    // Apply filters (called on button click or after a short delay when typing)
    const applyFilters = () => {
        if (activeTab === 'logs') {
            fetchAuditLogs();
        } else if (activeTab === 'security') {
            fetchSecurityEvents();
        }
    };

    // Handle export
    const handleExport = async (exportData) => {
        try {
            // Check authentication before export
            if (!localStorage.getItem('accessToken')) {
                navigate('/?expired=true');
                return;
            }

            setLoading(true);

            // Process the export data to match the backend expectations
            const processedData = {
                school_id: exportData.school_id || undefined,
                start_date: exportData.start_date,
                end_date: exportData.end_date,
                format: exportData.format || 'json'
            };

            const data = await auditAPI.exportAuditLogs(processedData);

            // Create a downloadable file based on format
            const fileName = `audit_logs_export_${new Date().toISOString().split('T')[0]}.${exportData.format}`;
            const fileContent = exportData.format === 'json'
                ? JSON.stringify(data.export_data || [], null, 2)
                : convertToCSV(data.export_data || []);

            const blob = new Blob([fileContent], {type: exportData.format === 'json' ? 'application/json' : 'text/csv'});
            const url = URL.createObjectURL(blob);

            // Create a temporary anchor element and trigger the download
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            setShowExportModal(false);
            showToast(`Successfully exported ${data.total_records || 0} logs to ${fileName}`, 'success');
        } catch (error) {
            console.error("Error exporting audit logs:", error);
            const errorMessage = auditAPI.handleError(error);
            setError(errorMessage);
            showToast(`Failed to export audit logs: ${errorMessage}`, 'error');

            // Check if it's an authentication error
            if (error.status === 401) {
                navigate('/?expired=true');
            }
        } finally {
            setLoading(false);
        }
    };

    // Convert data to CSV format
    const convertToCSV = (data) => {
        if (!data || !data.length) return '';

        const headers = Object.keys(data[0]);
        const csvRows = [
            headers.join(','), // Header row
            ...data.map(row =>
                headers.map(header => {
                    let cell = row[header];
                    // Handle objects and special characters
                    if (typeof cell === 'object' && cell !== null) {
                        cell = JSON.stringify(cell);
                    }
                    if (typeof cell === 'string') {
                        // Escape quotes and wrap in quotes if contains special chars
                        cell = cell.replace(/"/g, '""');
                        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
                            cell = `"${cell}"`;
                        }
                    }
                    return cell;
                }).join(',')
            )
        ];

        return csvRows.join('\n');
    };

    // Handle pagination
    const handlePageChange = (newSkip) => {
        if (activeTab === 'logs') {
            setFilters({...filters, skip: newSkip});
            fetchAuditLogs();
        } else if (activeTab === 'security') {
            setSecurityFilters({...securityFilters, skip: newSkip});
            fetchSecurityEvents();
        }
    };

    // Show toast notification
    const showToast = (message, type = 'success') => {
        setToast({show: true, message, type});
        setTimeout(() => setToast({show: false, message: '', type: 'success'}), 3000);
    };

    // Refresh data
    const handleRefresh = () => {
        if (activeTab === 'logs') {
            fetchAuditLogs();
        } else if (activeTab === 'security') {
            fetchSecurityEvents();
        } else if (activeTab === 'statistics') {
            fetchStatistics();
        }
    };

    // Loading state
    if (loading && !auditLogs.length && !securityEvents.length && !statistics) {
        return (
            <Layout pageTitle="Audit Logs">
                <div className="min-h-screen p-6 flex items-center justify-center">
                    <div className="text-center">
                        <div
                            className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading audit data...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    // Error state (for initial load)
    if (error && !auditLogs.length && !securityEvents.length && !statistics) {
        return (
            <Layout pageTitle="Audit Logs">
                <div className="min-h-screen p-6 flex items-center justify-center">
                    <div className="text-center max-w-md">
                        <div
                            className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <XCircle size={32} className="text-red-600"/>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">
                            Error Loading Audit Data
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {error}
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={handleRefresh}
                                className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors mr-3"
                            >
                                Try Again
                            </button>
                            {error.includes('Session expired') && (
                                <button
                                    onClick={() => navigate('/')}
                                    className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors"
                                >
                                    Go to Login
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Audit Logs">
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto space-y-6">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
                                <FileText size={32} className="text-orange-600"/>
                                Audit & Security Logs
                            </h1>
                            <p className="text-slate-600 mt-1">
                                Monitor activity and security events across all schools
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={handleRefresh}
                                className="bg-white text-gray-600 px-4 py-3 rounded-xl font-medium hover:bg-gray-50 transition-all border border-gray-200 flex items-center gap-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <Loader size={20} className="animate-spin"/>
                                ) : (
                                    <RefreshCw size={20}/>
                                )}
                                {loading ? 'Refreshing...' : 'Refresh'}
                            </button>

                            <button
                                onClick={() => setShowExportModal(true)}
                                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center gap-2 shadow-lg"
                            >
                                <Download size={20}/>
                                Export Logs
                            </button>
                        </div>
                    </div>

                    {/* Toast Notification */}
                    {toast.show && <Toast message={toast.message} type={toast.type}
                                          onClose={() => setToast({...toast, show: false})}/>}

                    {/* Error Alert */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                            <AlertTriangle className="text-red-500 mt-0.5 flex-shrink-0"/>
                            <div>
                                <h3 className="font-medium text-red-800">Error</h3>
                                <p className="text-red-700 text-sm">{error}</p>
                                <button
                                    className="text-red-600 text-sm font-medium mt-2 hover:text-red-700"
                                    onClick={() => setError(null)}
                                >
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Tabs */}
                    <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                        <div className="flex border-b border-gray-200">
                            <button
                                className={`px-6 py-4 font-medium flex items-center gap-2 transition-colors ${
                                    activeTab === 'logs'
                                        ? 'text-orange-600 border-b-2 border-orange-500 bg-orange-50'
                                        : 'text-gray-600 hover:bg-gray-50'
                                }`}
                                onClick={() => setActiveTab('logs')}
                            >
                                <FileText size={18}/>
                                Audit Logs
                            </button>
                            <button
                                className={`px-6 py-4 font-medium flex items-center gap-2 transition-colors ${
                                    activeTab === 'security'
                                        ? 'text-orange-600 border-b-2 border-orange-500 bg-orange-50'
                                        : 'text-gray-600 hover:bg-gray-50'
                                }`}
                                onClick={() => setActiveTab('security')}
                            >
                                <Shield size={18}/>
                                Security Events
                            </button>
                            <button
                                className={`px-6 py-4 font-medium flex items-center gap-2 transition-colors ${
                                    activeTab === 'statistics'
                                        ? 'text-orange-600 border-b-2 border-orange-500 bg-orange-50'
                                        : 'text-gray-600 hover:bg-gray-50'
                                }`}
                                onClick={() => setActiveTab('statistics')}
                            >
                                <Calendar size={18}/>
                                Statistics
                            </button>
                        </div>

                        {/* Filter Section */}
                        <div className="p-6 border-b border-gray-200 bg-gray-50">
                            {activeTab === 'logs' && (
                                <div className="flex flex-col lg:flex-row gap-4">
                                    <div className="flex-1 relative">
                                        <Search size={20}
                                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"/>
                                        <input
                                            type="text"
                                            placeholder="Search by user email..."
                                            value={filters.user_email}
                                            onChange={(e) => handleFilterChange({user_email: e.target.value})}
                                            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        />
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        <select
                                            value={filters.event_type}
                                            onChange={(e) => handleFilterChange({event_type: e.target.value})}
                                            className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        >
                                            <option value="">All Event Types</option>
                                            <option value="login">Login</option>
                                            <option value="logout">Logout</option>
                                            <option value="create">Create</option>
                                            <option value="update">Update</option>
                                            <option value="delete">Delete</option>
                                            <option value="access">Access</option>
                                            <option value="superadmin_operation">Superadmin Operation</option>
                                            <option value="data_access">Data Access</option>
                                        </select>

                                        <div className="relative">
                                            <DateRangePicker
                                                startDate={filters.start_date}
                                                endDate={filters.end_date}
                                                onChange={(startDate, endDate) => handleFilterChange({
                                                    start_date: startDate,
                                                    end_date: endDate
                                                })}
                                                placeholder="Select Date Range"
                                                className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                            />
                                        </div>

                                        <button
                                            onClick={applyFilters}
                                            className="px-4 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-medium"
                                        >
                                            Apply Filters
                                        </button>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'security' && (
                                <div className="flex flex-col lg:flex-row gap-4">
                                    <div className="flex-1 relative">
                                        <Search size={20}
                                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"/>
                                        <input
                                            type="text"
                                            placeholder="Search by alert type..."
                                            value={securityFilters.alert_type}
                                            onChange={(e) => handleSecurityFilterChange({alert_type: e.target.value})}
                                            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        />
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        <select
                                            value={securityFilters.severity}
                                            onChange={(e) => handleSecurityFilterChange({severity: e.target.value})}
                                            className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        >
                                            <option value="">All Severities</option>
                                            <option value="low">Low</option>
                                            <option value="medium">Medium</option>
                                            <option value="high">High</option>
                                            <option value="critical">Critical</option>
                                        </select>

                                        <div className="relative">
                                            <DateRangePicker
                                                startDate={securityFilters.start_date}
                                                endDate={securityFilters.end_date}
                                                onChange={(startDate, endDate) => handleSecurityFilterChange({
                                                    start_date: startDate,
                                                    end_date: endDate
                                                })}
                                                placeholder="Select Date Range"
                                                className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                            />
                                        </div>

                                        <button
                                            onClick={applyFilters}
                                            className="px-4 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-medium"
                                        >
                                            Apply Filters
                                        </button>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'statistics' && (
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-medium text-gray-700">Audit Statistics Dashboard</h3>
                                    <div className="flex gap-3">
                                        <select
                                            value={filters.school_id}
                                            onChange={(e) => {
                                                setFilters({...filters, school_id: e.target.value});
                                            }}
                                            className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        >
                                            <option value="">All Schools</option>
                                            {/* You would populate this with actual school data */}
                                            <option value="school1">School 1</option>
                                            <option value="school2">School 2</option>
                                            <option value="school3">School 3</option>
                                        </select>

                                        <button
                                            onClick={fetchStatistics}
                                            className="px-4 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-medium"
                                        >
                                            Get Statistics
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Content Area */}
                        <div className="p-6">
                            {activeTab === 'logs' && (
                                <LogsTable
                                    logs={auditLogs}
                                    loading={loading}
                                    totalLogs={totalLogs}
                                    currentPage={Math.floor(filters.skip / filters.limit) + 1}
                                    pageSize={filters.limit}
                                    onPageChange={(page) => handlePageChange((page - 1) * filters.limit)}
                                />
                            )}

                            {activeTab === 'security' && (
                                <SecurityEventsTable
                                    events={securityEvents}
                                    loading={loading}
                                    totalEvents={totalSecurityEvents}
                                    currentPage={Math.floor(securityFilters.skip / securityFilters.limit) + 1}
                                    pageSize={securityFilters.limit}
                                    onPageChange={(page) => handlePageChange((page - 1) * securityFilters.limit)}
                                />
                            )}

                            {activeTab === 'statistics' && (
                                <StatisticsPanel statistics={statistics} loading={loading}/>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Export Modal */}
            <ExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onExport={handleExport}
                schoolId={filters.school_id}
            />
        </Layout>
    );
};

export default AuditLogsPage;